/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';

/**
 * @useErrorPrompt
 * A hook to manage the state of an error prompt for IonGrid.
 * 
 * ```typescript
 * 
 * import useErrorPrompt from '@ion/dls/src/components/snackbars/useErrorPrompt';
 * 
 * const [showError, setShowError, onDismiss] = useErrorPrompt();
 * 
 * <IonGrid>
 *   ...
 *   <ErrorSnackbar show={showError} 
 *     message={translate('common.error')} 
 *     onDismiss={onDismiss} />
 *   ...
 * </IonGrid>
 * ```
 * 
 * @param show the boolean value to show the error prompt
 * @returns an array of the error prompt state, a function to set the error 
 * prompt state, and a function to dismiss the error prompt
 */
export const useErrorPrompt = (show = false) => {
  const [showError, setShowError] = useState(show);

  useEffect(() => {
    setShowError(show);
  }, [show]);
  
  const onDismiss = useCallback(() => {
    setShowError(false);
  }, []);

  return [showError, setShowError, onDismiss];
};

export default useErrorPrompt;
